import { tenantConfigSchema, type TenantConfig } from '../shared/schemas';

export interface Tenant {
  config: TenantConfig;
  /** Absolute URL of a file inside the tenant folder. */
  asset(rel: string): string;
}

export type TenantResult = { ok: true; tenant: Tenant } | { ok: false; reason: 'missing' | 'not-found' | 'invalid' };

export interface Route {
  slug: string | null;
  page: 'app' | 'privacy';
}

/** Tenant shown at the site root, set per deployment (VITE_DEFAULT_TENANT), e.g. one company's own subdomain. */
const DEFAULT_TENANT: string | null = import.meta.env.VITE_DEFAULT_TENANT || null;

/** Tenant comes from the first path segment (`/<slug>`), the `tenant` query parameter, or the deployment default. */
export function parseRoute(loc: { pathname: string; search: string }, fallback = DEFAULT_TENANT): Route {
  const parts = loc.pathname.split('/').filter(Boolean);
  const query = new URLSearchParams(loc.search).get('tenant');
  return { slug: parts[0] ?? query ?? fallback, page: parts[1] === 'privacy' ? 'privacy' : 'app' };
}

export async function loadTenant(slug: string | null): Promise<TenantResult> {
  if (slug === null) return { ok: false, reason: 'missing' };
  if (!/^[a-z0-9][a-z0-9-]*$/.test(slug)) return { ok: false, reason: 'not-found' };
  const base = new URL(`/tenants/${slug}/`, location.origin);
  let res: Response;
  try {
    res = await fetch(new URL(`config.json?v=${__BUILD_ID__}`, base));
  } catch {
    return { ok: false, reason: 'not-found' };
  }
  // SPA fallback servers answer unknown paths with index.html, so check the type too.
  if (!res.ok || !(res.headers.get('content-type') ?? '').includes('json')) return { ok: false, reason: 'not-found' };
  const parsed = tenantConfigSchema.safeParse(await res.json().catch(() => null));
  if (!parsed.success) {
    console.error('Invalid tenant config', parsed.error.issues);
    return { ok: false, reason: 'invalid' };
  }
  return { ok: true, tenant: { config: parsed.data, asset: (rel) => new URL(`${rel}?v=${__BUILD_ID__}`, base).href } };
}

/** Applies brand colors and font as CSS variables. */
export function applyTheme(cfg: TenantConfig) {
  const root = document.documentElement.style;
  root.setProperty('--fv-primary', cfg.brand.primary);
  root.setProperty('--fv-primary-text', cfg.brand.primaryText);
  root.setProperty('--fv-accent', cfg.brand.accent);
  root.setProperty('--fv-font', `"${cfg.brand.font}", ui-sans-serif, system-ui, sans-serif`);
  const id = 'fv-font';
  if (!document.getElementById(id)) {
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(cfg.brand.font)}:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }
  document.title = `${cfg.brand.name} Floor Visualizer`;
}

/** Fills `{brand}` placeholders in tenant copy. */
export function fillCopy(text: string, cfg: TenantConfig) {
  return text.replaceAll('{brand}', cfg.brand.name);
}
